import { z } from 'zod';
import { AuditSchema, DateOnlySchema, IdSchema, InstantSchema } from './common.ts';
import {
  MoneySchema,
  addMoney,
  allocateMoney,
  money,
  subtractMoney,
  type Money,
} from '../money.ts';
import { CostLineSchema, costTotals, lineBuilderCost, type CostLine } from './cost-line.ts';

/**
 * A bill: what somebody has asked the builder to pay.
 *
 * The reason it exists in Phase 5 is **`actual`** on the budget — money
 * spent rather than promised. It is also what finally makes `committed`
 * mean what `docs/features/07-costing-budget.md` §7 says: released purchase
 * order totals *less what has been billed against them*. P5-09a wrote that
 * subtraction with nothing to subtract.
 *
 * `docs/features/08-purchase-orders-bills.md` §3 is the screen. This file is
 * the shape and the arithmetic.
 */

/**
 * Where a bill has got to, as far as a **person** has decided.
 *
 * Three states and not five. `docs/DATA_MODEL.md` listed `paid` and
 * `partiallyPaid` alongside these, and they are not stored: they are what
 * the recorded payments add up to, and a stored one is a status that
 * eventually disagrees with the payments beneath it — which is the argument
 * somebody has with a subcontractor about whether an invoice was settled.
 * `billPaymentState` derives them.
 *
 * `readyForPayment` is a real state rather than a flag because it is a
 * decision somebody is accountable for: the `markReadyForPayment`
 * permission exists for it, and "who said this could be paid" has to be
 * answerable afterwards.
 */
export const BILL_STATUSES = ['open', 'readyForPayment', 'void'] as const;
export const BillStatusSchema = z.enum(BILL_STATUSES);
export type BillStatus = z.infer<typeof BillStatusSchema>;

/**
 * The transitions this product allows.
 *
 * `readyForPayment → open` is allowed: a bill approved for payment and then
 * queried is ordinary, and the way back must not be "void it and ask the
 * sub to send it again". Nothing leaves `void`, and there is no delete — a
 * bill number stays used.
 *
 * A bill with payments recorded against it cannot be voided; `canVoidBill`
 * says so rather than this table, because the answer depends on the
 * payments and not only on the status.
 */
export const BILL_TRANSITIONS: Readonly<Record<BillStatus, readonly BillStatus[]>> = {
  open: ['readyForPayment', 'void'],
  readyForPayment: ['open', 'void'],
  void: [],
};

export function canTransitionBill(from: BillStatus, to: BillStatus): boolean {
  return BILL_TRANSITIONS[from].includes(to);
}

export const PAYMENT_METHODS = ['check', 'card', 'ach', 'cash', 'other'] as const;
export const PaymentMethodSchema = z.enum(PAYMENT_METHODS);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  check: 'Cheque',
  card: 'Card',
  ach: 'Bank transfer',
  cash: 'Cash',
  other: 'Other',
};

/**
 * One payment recorded against a bill.
 *
 * Embedded on the bill rather than a collection of its own, which is what
 * `docs/DATA_MODEL.md` §6 has and is right here: a payment has no life
 * apart from its bill, it is read every time the bill is, and a bill has a
 * handful. Deposits and owner payments are a different thing and will have
 * their own records (P5-13).
 *
 * The id comes from the client, so a double-clicked `Record payment` is one
 * payment. `recordedBy` is the uid, because "who entered this" is the first
 * question asked when a figure is disputed.
 */
export const BillPaymentSchema = z.object({
  id: IdSchema,
  amount: MoneySchema,
  method: PaymentMethodSchema,
  at: InstantSchema,
  /** Cheque number, transfer reference — whatever ties it to a bank line. */
  reference: z.string().trim().max(120).optional(),
  recordedBy: IdSchema,
});
export type BillPayment = z.infer<typeof BillPaymentSchema>;

/**
 * A lien waiver on a bill (P5-10c).
 *
 * Four types, and they are two questions rather than four answers:
 * **conditional or unconditional** (does the waiver take effect only once
 * the payment clears), and **progress or final** (does it cover this
 * payment or everything). `docs/features/08-purchase-orders-bills.md` §3
 * lists them that way and a builder says them as a pair, so the type is a
 * single enum of the four combinations rather than two booleans — two
 * booleans would allow no illegal state here, but they read worse at every
 * call site and in every label.
 *
 * `amount` and `throughDate` are what the waiver actually covers, which is
 * **not** always the bill's own total: a progress waiver through the end of
 * last month against an invoice dated this month is ordinary. So they are
 * stored rather than derived from the bill.
 *
 * `required` is separate from "signed". A waiver that is not required is
 * not a missing waiver, and a job that treats the two the same will chase a
 * sub for a document nobody asked for.
 */
export const LIEN_WAIVER_TYPES = [
  'conditionalProgress',
  'conditionalFinal',
  'unconditionalProgress',
  'unconditionalFinal',
] as const;
export const LienWaiverTypeSchema = z.enum(LIEN_WAIVER_TYPES);
export type LienWaiverType = z.infer<typeof LienWaiverTypeSchema>;

export const LIEN_WAIVER_TYPE_LABEL: Record<LienWaiverType, string> = {
  conditionalProgress: 'Conditional, progress',
  conditionalFinal: 'Conditional, final',
  unconditionalProgress: 'Unconditional, progress',
  unconditionalFinal: 'Unconditional, final',
};

export const LienWaiverSchema = z.object({
  /** Whether this bill needs one at all. Not the same as "has one". */
  required: z.boolean().default(false),
  type: LienWaiverTypeSchema.default('conditionalProgress'),
  /** What the waiver covers, which need not be the bill's total. */
  amount: MoneySchema.optional(),
  /** Work through this date is covered. */
  throughDate: DateOnlySchema.optional(),
  /** The signed document, once it arrives. */
  fileId: IdSchema.optional(),
  signedAt: InstantSchema.optional(),
});
export type LienWaiver = z.infer<typeof LienWaiverSchema>;

export type LienWaiverState = 'notRequired' | 'awaited' | 'received';

/**
 * Where the waiver has got to.
 *
 * `received` needs the **file**, not the date: a `signedAt` with nothing
 * attached is somebody's note that a document exists, and the point of a
 * lien waiver is the document. Stated here rather than at each call site
 * because "do we have the waiver" is asked by the list, the modal and
 * eventually by whatever stops a payment going out without one.
 */
export function lienWaiverState(waiver: LienWaiver | undefined): LienWaiverState {
  if (!waiver?.required) return 'notRequired';
  return waiver.fileId ? 'received' : 'awaited';
}

export const BillSchema = AuditSchema.extend({
  id: IdSchema,
  companyId: IdSchema,
  jobId: IdSchema,
  /** `B-0001`: per job, zero-padded, allocated in a transaction. */
  number: z.string().trim().min(1).max(32),
  title: z.string().trim().min(1).max(200),
  /** Who is owed. Optional while somebody is still typing it in. */
  payToContactId: IdSchema.optional(),
  description: z.string().max(20_000).default(''),
  /** The date on the supplier's invoice, which is not the date it arrived. */
  invoiceDate: DateOnlySchema.optional(),
  dueDate: DateOnlySchema.optional(),
  /**
   * The purchase order this bills against.
   *
   * Optional, because a bill for something nobody raised a PO for is
   * ordinary — a delivery charge, a hire invoice. When it is set, it is
   * what lets `committed` fall as the work is billed; without it the bill
   * still counts towards `actual` and the PO stays fully committed, which
   * is the honest answer rather than a guess.
   */
  purchaseOrderId: IdSchema.optional(),
  status: BillStatusSchema.default('open'),
  lines: z.array(CostLineSchema).default([]),
  payments: z.array(BillPaymentSchema).default([]),
  /** Recharged to the owner on an invoice. P5-12 does the recharging. */
  billableToOwner: z.boolean().default(false),
  /**
   * Optional, and absent is not the same as `{ required: false }`.
   *
   * Absent means nobody has considered it; `required: false` means
   * somebody decided this bill does not need one. The difference is
   * whether a report of bills missing a waiver should list it.
   */
  lienWaiver: LienWaiverSchema.optional(),
  tagIds: z.array(IdSchema).default([]),
  source: z.enum(['manual', 'receipt', 'sub']).default('manual'),
});
export type Bill = z.infer<typeof BillSchema>;

/** What the bill is for, in money: the cost of its lines. */
export function billTotal(bill: Pick<Bill, 'lines'>, currency = 'USD'): Money {
  return costTotals(bill.lines, currency).cost;
}

/** What has been paid against it. */
export function billPaid(bill: Pick<Bill, 'payments'>, currency = 'USD'): Money {
  return bill.payments.reduce(
    (running, payment) => addMoney(running, payment.amount),
    money(0, currency),
  );
}

export function billOutstanding(bill: Pick<Bill, 'lines' | 'payments'>, currency = 'USD'): Money {
  return subtractMoney(billTotal(bill, currency), billPaid(bill, currency));
}

export type BillPaymentState = 'unpaid' | 'partial' | 'paid' | 'overpaid';

/**
 * What the payments add up to, as a label.
 *
 * Derived rather than stored — see `BILL_STATUSES`. `overpaid` is a state
 * rather than an error, because it happens: a cheque covering two bills
 * gets recorded against one. Reporting it is how somebody finds it; a
 * clamp would hide the mistake and leave the money unaccounted for.
 */
export function billPaymentState(bill: Pick<Bill, 'lines' | 'payments'>): BillPaymentState {
  const total = billTotal(bill).amount;
  const paid = billPaid(bill).amount;
  if (paid === 0) return 'unpaid';
  if (paid > total) return 'overpaid';
  if (paid >= total) return 'paid';
  return 'partial';
}

/**
 * Whether this bill may be voided.
 *
 * Not if anything has been paid against it. A voided bill counts towards
 * nothing, so voiding one that has been paid would take money out of
 * `actual` that has genuinely left the bank — and the bank statement would
 * then disagree with the job. The way out is a credit, which is P5-13.
 */
export function canVoidBill(bill: {
  readonly status: BillStatus;
  /** Only the count matters, so the server can pass what it read. */
  readonly payments: readonly unknown[];
}): boolean {
  return bill.status !== 'void' && bill.payments.length === 0;
}

/** Only an open bill's own fields are editable. Ready-for-payment is a decision. */
export function isBillEditable(status: BillStatus): boolean {
  return status === 'open';
}

/**
 * Accrual or cash, and it lives here rather than in `jobCosting`.
 *
 * `job-costing.ts` declared this type when it had nothing to do with it —
 * `actual` was structurally zero. Now that bills decide what the word
 * means, the definition belongs beside them, and `jobCosting` imports it.
 * Two declarations of one union is how they come to disagree.
 */
export const ACCOUNTING_METHODS = ['accrual', 'cash'] as const;
export const AccountingMethodSchema = z.enum(ACCOUNTING_METHODS);
export type AccountingMethod = z.infer<typeof AccountingMethodSchema>;

/**
 * What a bill contributes to `actual`, by cost code.
 *
 * **Accrual** counts the whole bill: the money is owed, and a job that has
 * taken delivery has spent it whether or not the cheque has gone out.
 *
 * **Cash** counts only what has been paid, apportioned across the lines
 * **pro rata by cost** with `allocateMoney`, so the parts add back to the
 * payment exactly. A partial payment is not attributable to particular
 * lines — nobody pays "the concrete half" of an invoice — so apportioning
 * it is the only answer that does not invent information, and doing it any
 * other way loses cents.
 *
 * A **void** bill contributes nothing on either method.
 *
 * A line with no cost code is counted under `null`, like every other
 * unattributable money in `jobCosting`, so the budget page can name it
 * rather than dropping it.
 */
export function actualByCostCode(
  bills: readonly Bill[],
  method: AccountingMethod,
  currency = 'USD',
): Map<string | null, Money> {
  const byCode = new Map<string | null, Money>();

  for (const bill of bills) {
    if (bill.status === 'void') continue;
    if (bill.lines.length === 0) continue;

    const weights = bill.lines.map((line) => Math.max(lineBuilderCost(line).amount, 0));
    const share =
      method === 'accrual'
        ? bill.lines.map((line) => lineBuilderCost(line))
        : // Nothing paid: nothing to apportion, and `allocateMoney` refuses
          // zero weights — a bill of zero-cost lines has nothing to split.
          weights.some((weight) => weight > 0)
          ? allocateMoney(billPaid(bill, currency), weights)
          : bill.lines.map(() => money(0, currency));

    bill.lines.forEach((line, index) => {
      const key = line.costCodeId ?? null;
      byCode.set(key, addMoney(byCode.get(key) ?? money(0, currency), share[index]!));
    });
  }

  return byCode;
}

/**
 * What has been billed against each purchase order, by cost code.
 *
 * Keyed by purchase order **and** cost code, because that is the shape
 * `committed` needs: a released PO's commitment falls as it is billed, and
 * the fall belongs to the code the **bill's** line names rather than the
 * PO's. Those can differ — a sub bills concrete against a code the PO put
 * under groundwork — and when they do, the bill's own coding is the one
 * that reached the ledger.
 *
 * Counted on the bill regardless of payment, on both accounting methods.
 * "Has this been billed" is not "has this been paid": a PO billed in full
 * and unpaid is no longer committed money, it is a debt, and leaving it
 * committed would double-count it against the budget.
 */
export function billedByPurchaseOrder(
  bills: readonly Bill[],
  currency = 'USD',
): Map<string, Map<string | null, Money>> {
  const byPo = new Map<string, Map<string | null, Money>>();

  for (const bill of bills) {
    if (bill.status === 'void') continue;
    if (!bill.purchaseOrderId) continue;
    const forPo = byPo.get(bill.purchaseOrderId) ?? new Map<string | null, Money>();
    for (const line of bill.lines) {
      const key = line.costCodeId ?? null;
      forPo.set(key, addMoney(forPo.get(key) ?? money(0, currency), lineBuilderCost(line)));
    }
    byPo.set(bill.purchaseOrderId, forPo);
  }

  return byPo;
}

/** The next bill number for a job, formatted the way it is filed. */
export function formatBillNumber(sequence: number): string {
  return `B-${String(sequence).padStart(4, '0')}`;
}

export const BILL_NUMBER_MAX = 9999;

/** The sequence out of a bill number, or `undefined`. */
export function parseBillNumber(raw: string): number | undefined {
  const trimmed = raw.trim();
  const tail = trimmed.slice(trimmed.lastIndexOf('-') + 1);
  if (!/^\d{1,4}$/.test(tail)) return undefined;
  const sequence = Number.parseInt(tail, 10);
  return sequence >= 1 && sequence <= BILL_NUMBER_MAX ? sequence : undefined;
}

/**
 * Whether a sub may see this bill.
 *
 * Only their own, and only one they submitted or that is addressed to them.
 * A bill carries what the builder is paying somebody; another sub's is a
 * competitor's price, the same reason a purchase order is private.
 */
export function billVisibleToSub(
  bill: Pick<Bill, 'payToContactId'>,
  contactId: string | undefined,
): boolean {
  if (!contactId) return false;
  return bill.payToContactId === contactId;
}

/** The lines a bill starts with when it is billing a purchase order. */
export function billLinesFromPurchaseOrder(
  purchaseOrderId: string,
  poLines: readonly CostLine[],
  makeId: () => string,
): CostLine[] {
  return poLines.map((line) => ({
    ...line,
    id: makeId(),
    // Provenance, not a live link: what was billed is a fact about the
    // past, so the figures are copies and this says where they came from.
    // Editing the purchase order afterwards does not rewrite the bill.
    sourceRef: { collection: 'purchaseOrders', id: purchaseOrderId, lineId: line.id },
  }));
}
